import React, { useEffect, useMemo, useRef, useState } from "react";
import ui from "@/components/add-v2/ui";
import css from "./.module.scss";
import { Button, Modal, Skeleton, Spinner } from "@aqar-app/react-ui";
import { ClassicModal } from "@/components/Listing/ListingScreen/classicModal";
import { FormController } from "@/components/add-v2/formController";
import { getDynamicValue, validate } from "@/components/add-v2/validate";
import Icon from "@/components/icon";
import logger from "@/console";

function AddSkeleton() {
  return (
    <div className={css.skeleton}>
      <Skeleton />
      <Skeleton />
      <Skeleton />
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </div>
  );
}
export const NewAddListingForm = function NewAddListingForm({
  steps = [],
  initial: initialProp,
  onFinish,
  loading,
  hideSteps,
  onStepChange,
  ...props
}) {
  const formRef = useRef(null);
  const [step, setStep] = useState(0);

  const currentStepRef = useRef(0);
  const [initial, setInitial] = useState(initialProp);
  const ctxRef = useRef(initial);

  useEffect(() => {
    if (!initialProp) return;
    ctxRef.current = {
      ...initialProp,
      ...ctxRef.current,
    };
    setInitial({
      ...initialProp,
      ...ctxRef.current,
    });
  }, [initialProp]);

  useEffect(() => {
    function visibilityListener(e) {
      const { visible, name, value, nested } = e.detail || {};

      logger.log("visibilitychange", { visible, name, value });

      /*
       *
       * based on this event we could for example mark
       * which fields should we remove before submit it to backend
       *
       * */

      const v = ctxRef.current;
      if (nested) {
        ctxRef.current = {
          ...v,
          [nested]: {
            ...(v[nested] || {}),
            [name]: value,
          },
        };
        return;
      }
      ctxRef.current = { ...v, [name]: value };
    }

    window.addEventListener("visibilitychange", visibilityListener);
    return () => {
      window.removeEventListener("visibilitychange", visibilityListener);
    };
  }, []);

  useEffect(() => {
    function handleChange(e) {
      const { name, value, nested } = e.detail || {};

      const v = ctxRef.current;
      if (nested) {
        ctxRef.current = {
          ...v,
          [nested]: {
            ...(v[nested] || {}),
            [name]: value,
          },
        };
        logger.log("CtxChange", { name, value, ctx: ctxRef.current });

        return;
      }
      ctxRef.current = {
        ...v,
        [name]: value,
      };
      logger.log("CtxChange", { name, value, ctx: ctxRef.current });
    }
    window.addEventListener("ctxchange", handleChange);

    return () => {
      window.removeEventListener("ctxchange", handleChange);
    };
  }, []);
  useEffect(() => {
    function ls(event) {
      const { value, e } = event.detail || {};
      const currentStep = currentStepRef.current;
      const ctx = ctxRef.current || {};

      /**
       * Next Step Case
       *
       * */
      if (value === "next") {
        if (!steps[currentStep]) return;
        const { rules = [] } = steps[currentStep].beforeNext || {};
        for (const rule of rules) {
          const { actions, ..._rule } = rule;
          const errorMessage = validate({
            rules: [_rule],
            ctx,
            label: "",
            currentValue: getDynamicValue(ctx, rule.path),
          });

          if (errorMessage) {
            const { next, cancel } = actions || {};
            return Modal.open(
              <ClassicModal
                title={rule.title?.ar || "خطـأ"}
                close={false}
                ok={
                  next
                    ? {
                        jsx: (
                          <Button
                            onClick={(e) => {
                              Modal.close(e);
                              const nextStep = steps[currentStep + 1];
                              if (!nextStep) return onFinish?.(ctx);
                              setStep(currentStep + 1);
                              currentStepRef.current = currentStep + 1;
                              if (typeof onStepChange == "function")
                                onStepChange(currentStepRef.current, {
                                  step: nextStep,
                                  ctx,
                                });
                            }}
                            {...next.props}
                          >
                            {next.text}
                          </Button>
                        ),
                      }
                    : null
                }
                cancel={
                  cancel
                    ? {
                        jsx: (
                          <Button onClick={Modal.close} {...cancel.props}>
                            {cancel.text}
                          </Button>
                        ),
                      }
                    : null
                }
                body={errorMessage}
              />,
            )(e);
          }
        }

        for (const st of steps[currentStep].fields) {
          const control =
            formRef.current.querySelector(`[name="${st.name}"]`) ||
            formRef.current.querySelector(`[data-name="${st.name}"]`);

          if (!control || st?.hide) continue;
          let currentValue = ctx[control.name];

          const errorMessage = validate({
            rules: st.rules,
            label: "",
            ctx,
            currentValue,
            fieldName: st.name,
          });
          logger.log("[validation] onFormNext", {
            name: control.name,
            message: errorMessage,
            value: currentValue,
            cv: ctx[control.name],
            ctx,
          });

          if (errorMessage) {
            control.dispatchEvent(
              new CustomEvent("validate", {
                detail: { error: errorMessage },
                bubbles: true,
              }),
            );

            return control.focus();
          }
        }
        const nextStep = steps[currentStep + 1];

        if (!nextStep) return onFinish?.(ctx);
        setStep(currentStep + 1);
        currentStepRef.current = currentStep + 1;
        if (typeof onStepChange == "function")
          onStepChange(currentStepRef.current, { step: nextStep, ctx });
      } else if (value === "previous") {
        /**
         * Previous Step Case
         *
         * */
        if (currentStep < 1) return;
        setStep(currentStep - 1);
        currentStepRef.current = currentStep - 1;
        if (typeof onStepChange == "function")
          onStepChange(currentStepRef.current, {
            step: steps[currentStep - 1],
            ctx: ctxRef.current,
          });
      } else if (+value <= steps.length - 1) {
        /**
         * Jump to  Step
         *
         * */
        setStep(value);
        currentStepRef.current = value;
      }
    }

    window.addEventListener("stepchange", ls);

    return () => window.removeEventListener("stepchange", ls);
  }, [steps, onFinish, onStepChange]);

  const Fields = useMemo(
    () => steps[step]?.fields.map((f) => [ui[f.ui || f.type], f]) || [],
    [steps, step],
  );

  return (
    <div className={css.wrapper}>
      {hideSteps || !steps.length || loading ? null : (
        <div
          style={{
            "--step": step,
            "--step-width": Math.round(100 / (steps.length - 1)) + "%",
          }}
          className={css.steps}
        >
          <div className={css.titles}>
            {steps.map((s, i) => (
              <div key={i}>
                <div> {s.title?.ar}</div>
              </div>
            ))}
          </div>

          <div className={css.indicatorWrapper}>
            {steps.map((s, i) => (
              <div key={i} aria-current={i <= step} className={css.indicator}>
                {i < step ? (
                  <Icon width={16} height={16} name="check-green" />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={formRef} className={css.card}>
        {!initial || loading || !steps.length ? (
          <AddSkeleton />
        ) : (
          Fields.map(([Comp, field]) => (
            <FormController
              field={field}
              key={field.name}
              ctxRef={ctxRef}
              value={
                field.nested
                  ? ctxRef.current[field.nested]?.[field.name] ||
                    initial[field.nested]?.[field.name]
                  : ctxRef.current[field.name] || initial[field.name]
              }
              Component={Comp}
            />
          ))
        )}
      </div>
    </div>
  );
};

NewAddListingForm.setStep = function setStep(data, e) {
  document.dispatchEvent(
    new CustomEvent("stepchange", {
      detail: {
        value: data,
        e,
      },
      bubbles: true,
    }),
  );
};
NewAddListingForm.next = function next(e) {
  NewAddListingForm.setStep("next", e);
};
NewAddListingForm.previous = function previous(e) {
  NewAddListingForm.setStep("previous", e);
};
NewAddListingForm.disable = function disable(name, value) {
  document.dispatchEvent(
    new CustomEvent("ctxuievent", {
      detail: {
        type: "disable",
        name,
        value,
      },
      bubbles: true,
    }),
  );
};
NewAddListingForm.loading = function disable(name, value) {
  document.dispatchEvent(
    new CustomEvent("ctxuievent", {
      detail: {
        type: "loading",
        name,
        value,
      },
      bubbles: true,
    }),
  );
};
NewAddListingForm.ActionButton = React.forwardRef(function Previous(
  { children, name, ...props },
  ref,
) {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    function handleChange(e) {
      const { type, value, name: n } = e.detail || {};
      if (type === "disable" && n === name) {
        return setDisabled(value);
      }
      if (type === "loading") {
        setDisabled(value);
        if (n === name) setLoading(value);
      }
    }
    window.addEventListener("ctxuievent", handleChange);

    return () => {
      window.removeEventListener("ctxuievent", handleChange);
    };
  }, [name]);
  return (
    <Button ref={ref} disabled={disabled || loading} {...props}>
      {loading ? <Spinner size="small" /> : null}
      {children}
    </Button>
  );
});
